import React from 'react';
import FeaturePage from './FeaturePage';
import { Box, Container, Typography, Paper, Grid, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CalculateIcon from '@mui/icons-material/Calculate';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BuildIcon from '@mui/icons-material/Build';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import { motion } from 'framer-motion';

import ocrScanScreenshot from '../assets/ocr-scan-screenshot.png'; // ** Placeholder: Create a relevant screenshot **
import dealAnalysisScreenshot from '../assets/deal-analysis-screenshot.png'; // ** Placeholder: Create a relevant screenshot **
import maintenanceRecordsScreenshot from '../assets/maintenance-records-screenshot.png'; // Placeholder for maintenance screenshot

// Animation Variants (borrowed from other feature pages)
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: "easeOut" } }
};


function DealAnalysisPage() {

  const ocrFeatures = [
    { icon: <CameraAltIcon color="primary"/>, text: "Scan Receipts Instantly: Use your device's camera to capture expense documents." },
    { icon: <AutoFixHighIcon color="primary"/>, text: "AI-Powered Data Extraction: Our industry-first OCR technology reads and understands receipt details." },
    { icon: <ReceiptLongIcon color="primary"/>, text: "Automated Field Population: Key information like date, amount, vendor, and category suggestions are automatically filled." },
    { icon: <SpeedIcon color="primary"/>, text: "Minimize Manual Entry: Drastically reduce time spent typing in expense details." },
    { icon: <CheckCircleOutlineIcon color="primary"/>, text: "Improve Accuracy: Eliminate typos and ensure precise cost recording for every vehicle." },
  ];

  const analysisFeatures = [
     { icon: <CalculateIcon color="success"/>, text: "Accurate Cost Aggregation: Automatically combines purchase price with all scanned and manually entered expenses." },
     { icon: <ReceiptLongIcon color="success"/>, text: "Real-time Total Investment: Know your exact investment in each vehicle at any moment." },
     { icon: <CalculateIcon color="success"/>, text: "Precise Profit Calculation: Determine true gross profit by subtracting the total investment from the sale price." },
     { icon: <ShowChartIcon color="success"/>, text: "Data-Driven Pricing: Make informed decisions on sale pricing based on accurate cost data." },
  ];

  const maintenanceFeatures = [
    { icon: <BuildIcon color="info"/>, text: "Detailed Service History: Log every repair, service, and part replacement for each vehicle." },
    { icon: <ReceiptLongIcon color="info"/>, text: "Organized Expense Tracking: Keep maintenance costs separate yet integrated into the total vehicle investment." },
    { icon: <CalculateIcon color="info"/>, text: "Total Maintenance Cost Summary: See the aggregated cost of all maintenance activities at a glance." },
    { icon: <CheckCircleOutlineIcon color="info"/>, text: "Easy Record Management: Add, edit, or delete maintenance records effortlessly." },
    { icon: <LocalPrintshopIcon color="info"/>, text: "Printable Reports: Generate clean reports of maintenance history for documentation or buyers." },
 ];


  return (
    <FeaturePage title="Deal Analysis & Maintenance Records">
      <Container maxWidth="xl" disableGutters sx={{ py: { xs: 4, md: 6 } }}>

        {/* Introductory Text */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          style={{ marginBottom: '4rem', textAlign: 'center' }}
        >
          <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Total Cost Clarity: Analysis, OCR & Maintenance.
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '850px', mx: 'auto' }}>
            DealCost combines groundbreaking OCR for expense capture with detailed maintenance logs and powerful analysis tools, giving you unparalleled insight into each vehicle's true cost and profitability.
          </Typography>
        </motion.div>

        {/* Section 1: OCR Capability */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 6, md: 8 } }}>
          <motion.div variants={imageVariants} sx={{ mb: 4 }}>
             {/* ** Replace with a screenshot showing the scan button or the process ** */}
             <Paper
               elevation={6}
               sx={{
                 overflow: 'hidden',
                 borderRadius: '12px',
                 border: '1px solid rgba(0,0,0,0.08)',
                 maxWidth: '500px',
                 mx: 'auto'
               }}
             >
               <Box component="img" src={ocrScanScreenshot} alt="DealCost OCR Scanning" sx={{ width: '100%', display: 'block' }}/>
             </Paper>
             <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                Turn paper receipts into digital records instantly with AI-powered scanning.
             </Typography>
          </motion.div>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, textAlign: 'center' }}>Industry-First Receipt Scanning</Typography>
             <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
               Stop wasting time manually entering expense data. Our intelligent OCR reads receipts for you, extracting crucial details and integrating them directly into your vehicle cost analysis. This capability is implemented using the "Scan Document" feature within the expense entry form.
             </Typography>
            <List>
              {ocrFeatures.map((feature, index) => (
                <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                  <ListItem disablePadding sx={{ mb: 2, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
        </motion.div>

        <Divider sx={{ my: { xs: 4, md: 8 }, borderColor: 'rgba(0,0,0,0.08)' }} />

        {/* Section 2: Integration into Deal Analysis */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 6, md: 8 } }}>
           <motion.div variants={imageVariants} sx={{ mb: 4 }}>
             {/* ** Replace with a screenshot showing the ViewDeal financial summary/chart ** */}
             <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
               <Box component="img" src={dealAnalysisScreenshot} alt="DealCost Deal Analysis View" sx={{ width: '100%', display: 'block' }}/>
             </Paper>
             <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                Accurate costs automatically feed into your deal analysis and profit calculations.
             </Typography>
           </motion.div>

           <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
             <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, textAlign: 'center' }}>From Scan to Strategy</Typography>
             <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
                Every scanned expense seamlessly integrates with the vehicle's record. DealCost automatically calculates the total investment, providing a precise foundation for analyzing profitability and making smarter pricing decisions.
              </Typography>
             <List>
               {analysisFeatures.map((feature, index) => (
                 <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                   <ListItem disablePadding sx={{ mb: 2, alignItems: 'flex-start' }}>
                     <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                       {feature.icon}
                     </ListItemIcon>
                     <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                   </ListItem>
                 </motion.div>
               ))}
             </List>
           </Box>
        </motion.div>

        <Divider sx={{ my: { xs: 4, md: 8 }, borderColor: 'rgba(0,0,0,0.08)' }} />

        {/* Section 3: Maintenance Records */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 4, md: 6 } }}>
           <motion.div variants={imageVariants} sx={{ mb: 4 }}>
             {/* ** Replace with a screenshot showing the maintenance table ** */}
             <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
               <Box component="img" src={maintenanceRecordsScreenshot} alt="DealCost Maintenance Records Table" sx={{ width: '100%', display: 'block' }}/>
             </Paper>
             <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                Keep a detailed log of all maintenance and repairs for full cost transparency.
             </Typography>
           </motion.div>

           <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
             <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, textAlign: 'center' }}>Comprehensive Maintenance Tracking</Typography>
             <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
                Document every service performed on a vehicle, from oil changes to major repairs. These costs are automatically included in the total investment calculation, ensuring you never miss an expense when determining profitability.
              </Typography>
             <List>
               {maintenanceFeatures.map((feature, index) => (
                 <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                   <ListItem disablePadding sx={{ mb: 2, alignItems: 'flex-start' }}>
                     <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                       {feature.icon}
                     </ListItemIcon>
                     <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                   </ListItem>
                 </motion.div>
               ))}
             </List>
           </Box>
        </motion.div>

      </Container>
    </FeaturePage>
  );
}

export default DealAnalysisPage;